import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import { Tooltip } from 'antd';
import { Components } from 'react-markdown';

// Styles for citation components
const citationStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.2rem;
  height: 1.2rem;
  padding: 0 0.3rem;
  margin: 0 0.2rem;
  border-radius: 6px;
  background-color:rgb(103, 103, 103);
  color:rgb(255, 255, 255);
  font-size: 0.7rem;
  font-weight: bold;
  cursor: pointer;
  vertical-align: text-top;
  text-decoration: none;
`;

const tooltipContentStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const faviconStyle = css`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

/**
 * CitationLink - Renders links as citation numbers with tooltips
 */
export const CitationLink: React.FC<{
  href?: string;
  children: React.ReactNode;
  citationMap: Map<string, number>;
}> = ({ href, children, citationMap }) => {
  if (!href) return <span>{children}</span>;
  
  // Get domain for the favicon
  const urlObj = new URL(href);
  const domain = urlObj.hostname;
  
  // Get or assign sequential citation number based on order of appearance
  let citationNumber;
  if (citationMap.has(href)) {
    citationNumber = citationMap.get(href);
  } else {
    citationNumber = citationMap.size + 1;
    citationMap.set(href, citationNumber);
  }
  
  // Extract title from the link text - typically the domain or site name
  const sourceTitle = typeof children === 'string' ? children : domain;
  
  // Initial tooltip content with basic info
  const [tooltipContent, setTooltipContent] = useState(
    <div className={tooltipContentStyle}>
      <span><strong>{sourceTitle}</strong></span>
      <span>{domain}</span>
    </div>
  );
  
  useEffect(() => {
    const fetchFavicon = async () => {
      try {
        // Create full favicon URL using Google's favicon service
        const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
        
        // Enhanced tooltip with favicon and better formatting
        setTooltipContent(
          <div className={tooltipContentStyle}>
            <img src={faviconUrl} alt="" className={faviconStyle} />
            <div>
              <div><strong>{sourceTitle}</strong></div>
              <div style={{ fontSize: '0.85em', color: '#666' }}>{href}</div>
            </div>
          </div>
        );
      } catch (error) {
        console.error('Error fetching favicon:', error);
      }
    };
    
    fetchFavicon();
  }, [href, domain, sourceTitle]);
  
  return (
    <Tooltip 
      title={tooltipContent}
      placement="top"
      overlayStyle={{ maxWidth: '300px' }}
    >
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        className={citationStyle}
        onClick={(e) => {
          // Open in a new tab
          window.open(href, '_blank', 'noopener,noreferrer');
          e.preventDefault();
        }}
      >
        {citationNumber}
      </a>
    </Tooltip>
  );
};

/**
 * Create a components object for use with ReactMarkdown
 */
export const createMarkdownComponents = (citationMap: Map<string, number>): Components => {
  return {
    a: ({ node, ...props }) => <CitationLink {...props} citationMap={citationMap} />,
    // Add other custom components here as needed
    // img: ({ node, ...props }) => <CustomImage {...props} />,
    // h1: ({ node, ...props }) => <CustomHeading {...props} level={1} />,
  };
};
